import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  MUIDataTableColumnDef,
  SelectableRows,
  MUISortOptions,
} from "mui-datatables";
import { saveAs } from "file-saver";

import Tooltip from "@material-ui/core/Tooltip";
import Grid from "@material-ui/core/Grid";
import moment from "moment";
import Card from "@material-ui/core/Card/Card";
import IconButton from "@material-ui/core/IconButton";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import Fab from "@material-ui/core/Fab";
import AddIcon from "@material-ui/icons/Add";

import GenericTemplate from "../../../templates/GenericTemplate";
import {
  apollo_useStyles,
  reviews_csv_status_zeus,
  valueToOption,
} from "../../../atom/Apollo_styles";
import {
  get_zeus_review_csv_path,
  review_csv_request_path,
} from "../../../atom/Apollo_links";
import {
  ApolloMessage,
  CustomizedSnackbars,
} from "../../../atom/Apollo_Mesasge";
import { ApolloContext } from "../../../atom/Apollo_Context";
import { optionsZeusDeliveryType } from "../../../common/Apollo_CommonConst";
import { ApolloDataTable } from "../../../atom/Apollo_DataTable";
import { ApolloAxios } from "../../../atom/Apollo_ApiRequest3";
import {
  ApolloBreadcrumbs2,
  BreadcrumbsData,
} from "../../../atom/Apollo_Breadcrumbs2";

import {
  ReviewCsvZeus,
  ReviewCsvResultAdCampaigns,
  ReviewCsvResultAdGroups,
} from "./ReviewCsvModel";
import {APOLLO_PAGE_ID} from "../../../common/Apollo_LocalStorageSettings";

/*
  画面名：審査結果ダウンロード
*/

type CsvName = {
  value: string;
  label: string;
};

const ReviewCsvPageZeus: React.FC = () => {
  const { state } = useContext(ApolloContext);
  const { data, setmsg } = useContext(ApolloMessage);

  const { accountgroupid, accountid, platformaccountid } = useParams<{
    accountgroupid: string;
    accountid: string;
    platformaccountid: string;
  }>();

  const classes = apollo_useStyles();
  const link = `${review_csv_request_path}zeus/${accountgroupid}/${accountid}/${platformaccountid}`;

  const [reviewCsv, setReviewCsv] = useState<ReviewCsvZeus[]>([]);
  const [platformAccountName, setPlatformAccountName] = useState("");
  const [reviewCsvNames, setReviewCsvNames] = useState<CsvName[]>([]);

  // --- フラグで一度だけ fetch ---
  const [hasFetchedCsv, setHasFetchedCsv] = useState(false);

  const breadcrumbsData: BreadcrumbsData = {
    categoryId: 27,
    breadId: 1,
    queries: [],
    menu: {
      id: 1,
      accountId: platformaccountid,
      accountName: "",
    },
    campaign: null,
    adgroup: null,
    advertisement: null,
    asset: null,
  };

  const downloadCsvFile = async (url: string) => {
    const baseUrl = url.replace(/\?.*$/, "");
    try {
      const response = await fetch(baseUrl);
      const blob = await response.blob();
      saveAs(blob, valueToOption(url, reviewCsvNames)?.label);
    } catch (e) {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: "ファイルダウンロードに失敗しました",
        },
      });
      return;
    }
  };

  const fetchCsvList = useCallback(async () => {
    setPlatformAccountName(state.current.accountName);
    if (hasFetchedCsv || !platformAccountName) return;

    const url = `${process.env.REACT_APP_API_ENDPOINT}${get_zeus_review_csv_path}/${platformaccountid}`;
    try {
      const res = await ApolloAxios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${state.accesstoken}`,
        },
      });
      const resultList: ReviewCsvZeus[] = res.data?.result ?? [];
      setReviewCsv(resultList);

      const list: CsvName[] = resultList
        .filter((r) => r.downloadFileUrl)
        .map((r) => ({
          value: r.downloadFileUrl!,
          label: `${
            r.id
          }_review_result_summary_${accountid}_${platformAccountName}_${moment(
            r.requestedAt
          ).format("YYYYMMDDHHmmss")}.csv`,
        }));
      setReviewCsvNames(list);
    } catch (error) {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: (error as any).type,
          message: (error as any).errmsg,
        },
      });
    } finally {
      setHasFetchedCsv(true);
    }
  }, [
    platformaccountid,
    platformAccountName,
    state.accesstoken,
    data,
    setmsg,
    hasFetchedCsv,
  ]);

  useEffect(() => {
    fetchCsvList();
  }, [platformAccountName, fetchCsvList]);

  // statusのカラム位置
  const STATUS_COLUMN_INDEX = 6;

  const columns = useMemo<MUIDataTableColumnDef[]>(
    () => [
      {
        name: "id",
        label: "審査結果リクエストID",
        options: { filter: false, sort: false },
      },
      {
        name: "deliveryTypes",
        label: "キャンペーン種別",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: number[]) => {
            if (!value?.length) return <span>-</span>;
            const labels = value.map(
              (v) =>
                optionsZeusDeliveryType.find((o) => o.value === v)?.label ?? "-"
            );
            return (
              <div>
                {labels.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              </div>
            );
          },
        },
      },
      {
        name: "adCampaigns",
        label: "キャンペーン",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: ReviewCsvResultAdCampaigns[]) => {
            if (!value?.length) return <span>-</span>;
            const first = value[0];
            const firstLabel = `${first.adCampaignId} | ${first.adCampaignName}`;
            if (value.length === 1) return <span>{firstLabel}</span>;
            const others = value
              .slice(1)
              .map((v) => `${v.adCampaignId} | ${v.adCampaignName}`)
              .join("\n");
            return (
              <>
                {firstLabel}{" "}
                <Tooltip
                  interactive
                  enterDelay={100}
                  leaveDelay={100}
                  title={
                    <pre
                      style={{
                        margin: 0,
                        maxWidth: "400px",
                        maxHeight: "200px",
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {others}
                    </pre>
                  }
                  arrow
                >
                  <span style={{ color: "#080808ff", cursor: "pointer" }}>
                    他
                  </span>
                </Tooltip>
              </>
            );
          },
        },
      },
      {
        name: "adGroups",
        label: "広告グループ",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: ReviewCsvResultAdGroups[]) => {
            if (!value?.length) return <span>-</span>;

            const first = value[0];
            const firstLabel = `${first.AdGroupId} | ${first.adGroupName}`;

            if (value.length === 1) return <span>{firstLabel}</span>;

            const others = value
              .slice(1)
              .map((v) => `${v.AdGroupId} | ${v.adGroupName}`)
              .join("\n");

            return (
              <>
                {firstLabel}{" "}
                <Tooltip
                  interactive
                  enterDelay={100}
                  leaveDelay={100}
                  title={
                    <pre
                      style={{
                        margin: 0,
                        maxWidth: "400px",
                        maxHeight: "200px",
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {others}
                    </pre>
                  }
                  arrow
                >
                  <span style={{ color: "#080808ff", cursor: "pointer" }}>
                    他
                  </span>
                </Tooltip>
              </>
            );
          },
        },
      },
      {
        name: "archiveFlag",
        label: "アーカイブ",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: boolean) => (
            <span>
              {value === true ? "含む" : value === false ? "含めない" : "-"}
            </span>
          ),
        },
      },
      {
        name: "requestedAt",
        label: "作成リクエスト日時",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: number) =>
            moment(value).format("YYYY/MM/DD HH:mm"),
        },
      },
      {
        name: "status",
        label: "作成状況",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: number) =>
            valueToOption(value, reviews_csv_status_zeus).label,
        },
      },
      {
        name: "downloadFileUrl",
        label: "ダウンロード",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: string, tableMeta) => {
            const status = tableMeta.rowData[STATUS_COLUMN_INDEX];

            // status:2(成功)以外または値がない場合は非活性
            const isEnabled = status === 2 && !!value;
            return (
              <IconButton
                aria-label="download"
                disabled={!isEnabled}
                onClick={() => {
                  if (isEnabled) {
                    downloadCsvFile(value);
                  }
                }}
              >
                <CloudDownloadIcon color={isEnabled ? "primary" : "disabled"} />
              </IconButton>
            );
          },
        },
      },
    ],
    [downloadCsvFile]
  );

  const options = useMemo(
    () => ({
      filter: false,
      download: false,
      print: false,
      search: false,
      sort: false,
      viewColumns: false,
      selectableRows: "none" as SelectableRows,
      pagination: true,
      rowsPerPage: 10,
      jumpToPage: true,
      apolloPageId: APOLLO_PAGE_ID.REVIEW_RESULT_DOWNLOAD_LIST,
      textLabels: { body: { noMatch: "該当データが存在しません" } },
      sortOrder: { name: "id", direction: "desc" } as MUISortOptions,
    }),
    []
  );

  return (
    <GenericTemplate title="">
      <CustomizedSnackbars />
      <ApolloBreadcrumbs2 data={breadcrumbsData} />
      <Grid container spacing={0}>
        <Tooltip title="審査結果ダウンロードリクエスト作成" placement="bottom">
          <Link to={link} className={classes.link}>
            <Fab
              size="small"
              color="primary"
              aria-label="add"
              style={{ marginTop: "10px" }}
            >
              <AddIcon />
            </Fab>
          </Link>
        </Tooltip>
      </Grid>
      <Card className={classes.root} square variant="outlined">
        <ApolloDataTable
          data={reviewCsv}
          columns={columns}
          options={options}
          title=""
        />
      </Card>
    </GenericTemplate>
  );
};

export default ReviewCsvPageZeus;
